import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  FiCheck, FiX, FiSearch, FiUserPlus, FiEdit2, FiTrash2,
  FiCheckCircle, FiAlertCircle, FiUsers, FiClock, FiMail, FiPhone, FiMapPin, FiPlus,
  FiFileText, FiDownload, FiCalendar, FiGift,
} from 'react-icons/fi';
import {
  getUsers, getDerasar, approveDerasarMember,
  getGuestMembers, addGuestMember, updateGuestMember, deleteGuestMember,
  createDerasarPerson,
  getDerasarPersonsAdmin, getAddressBookList,
  getMemberDirectory, exportReportTable,
  getDerasarMembers,
} from '../../services/api';
import PdfOptionsModal from '../../pdf/PdfOptionsModal';
import { useAuth } from '../../context/AuthContext';
import styles from './MyDerasar.module.css';
import { formatDate, normalizeDateOnly } from '../../utils/dateUtils';

// -- Helpers -------------------------------------------------------------------
const getInitials = (name) => (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

const formatAddress = (obj) => {
  if (!obj) return '';
  if (!obj.flatNo && !obj.buildingName && !obj.addressLine1) return '';
  return [
    obj.flatNo && obj.buildingName ? `${obj.flatNo}, ${obj.buildingName}` : (obj.flatNo || obj.buildingName),
    obj.addressLine1, obj.addressLine2,
    obj.city, obj.state,
    obj.pincode ? `- ${obj.pincode}` : '',
    obj.country && obj.country !== 'India' ? obj.country : '',
  ].filter(Boolean).join(', ');
};

const resolveAddress = (p) => {
  if (p.addressBookEntry) return formatAddress(p.addressBookEntry);
  return formatAddress(p);
};

const resolveAddressLabel = (p) => p.addressBookEntry?.label || null;

const EMPTY_PERSON_FORM = {
  headUserId: '',
  name: '', phone: '', email: '', panCard: '', aadhaarCard: '',
  addressBookId: '',
  specialDates: [],
};

// -- SearchableMemberSelect ----------------------------------------------------
const SearchableMemberSelect = ({ members, value, onChange, placeholder = 'Type to search member…' }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const selected = members.find((m) => String(m.id) === String(value));
  const displayVal = isOpen ? search : (selected ? selected.name : '');
  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    return (m.name || '').toLowerCase().includes(q)
      || (m.email || '').toLowerCase().includes(q)
      || (m.phone || '').toLowerCase().includes(q);
  });
  return (
    <div style={{ position: 'relative' }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false); }}>
      <input
        className="form-control"
        placeholder={placeholder}
        value={displayVal}
        onChange={(e) => { setSearch(e.target.value); setIsOpen(true); onChange(''); }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 250, overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '8px 12px', color: '#6b7280' }}>No members found</div>
            : filtered.map((m) => (
              <div key={m.id} tabIndex={0}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(m.id); setIsOpen(false); setSearch(''); }}
              >
                <div style={{ fontWeight: 500 }}>{m.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{m.email}{m.phone && ` · ${m.phone}`}</div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

// -- PendingRequestsSection ----------------------------------------------------
const PendingRequestsSection = ({ requests, actionLoading, onApprove }) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionHeader}>
      <FiClock size={15} />
      Pending Requests
      <span className={styles.countBadge}>{requests.length}</span>
    </div>
    <div className={styles.memberTableContainer}>
      <table className={styles.memberTable}>
        <thead>
          <tr>
            <th>Member</th>
            <th className={styles.actionsCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((u) => (
            <tr key={u.id}>
              <td>
                <div className={styles.memberCell}>
                  <div className={styles.memberAvatar}>{getInitials(u.name)}</div>
                  <div className={styles.memberDropdownItemInfo}>
                    <div className={styles.memberDropdownItemName}>{u.name}</div>
                    <div className={styles.memberDropdownItemEmail}>
                      {u.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><FiPhone size={10} /> {u.phone}</span>}
                      {u.phone && u.email && <span style={{ opacity: 0.4, margin: '0 4px' }}>·</span>}
                      {u.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><FiMail size={10} /> {u.email}</span>}
                      {u.isEmailVerified && <span style={{ color: 'var(--color-success)', marginLeft: 4 }} title="Verified"><FiCheckCircle size={11} /></span>}
                    </div>
                    {u.address && (
                      <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 3, marginTop: 3, fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                        <FiMapPin size={10} style={{ marginTop: 2, flexShrink: 0 }} />
                        {u.address}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className={styles.actionsCell}>
                <div className={styles.memberActions}>
                  <button className="btn btn-success btn-sm" onClick={() => onApprove(u.id, 'approve')} disabled={actionLoading === u.id}>
                    <FiCheck size={12} /> Approve
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => onApprove(u.id, 'reject')} disabled={actionLoading === u.id}>
                    <FiX size={12} /> Reject
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// -- PersonRow -----------------------------------------------------------------
const PersonRow = ({ person, index, isExpanded, onToggle }) => {
  const phone = person.phone || person.createdByMember?.phone;
  const email = person.email || person.createdByMember?.auth?.email;
  const phoneViaHead = !person.phone && person.createdByMember?.phone;
  const emailViaHead = !person.email && person.createdByMember?.auth?.email;
  const addr = resolveAddress(person);
  const addrLabel = resolveAddressLabel(person);
  const memberName = person.createdByMember?.name || `#${person.createdByMemberId}`;

  return (
    <React.Fragment>
      <tr className={styles.memberRowClickable} onClick={onToggle}>
        <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{index + 1}</td>
        <td>
          <div className={styles.memberCell}>
            <div className={styles.memberAvatar}>{getInitials(person.name)}</div>
            <div className={styles.memberDropdownItemInfo}>
              <div className={styles.memberDropdownItemName}>{person.name}</div>
              <div className={styles.memberDropdownItemEmail}>
                {phone && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiPhone size={10} /> {phone}
                    {person.isPhoneVerified ? (
                      <FiCheckCircle size={10} style={{ color: 'var(--color-success)' }} title="Phone verified" />
                    ) : (
                      <FiAlertCircle size={10} style={{ color: 'var(--color-danger)' }} title="Phone not verified" />
                    )}
                    {phoneViaHead && <em style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginLeft: 2 }}>(via {memberName})</em>}
                  </span>
                )}
                {phone && email && <span style={{ opacity: 0.4, margin: '0 4px' }}>·</span>}
                {email && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiMail size={10} /> {email}
                    {person.isEmailVerified ? (
                      <FiCheckCircle size={10} style={{ color: 'var(--color-success)' }} title="Email verified" />
                    ) : (
                      <FiAlertCircle size={10} style={{ color: 'var(--color-danger)' }} title="Email not verified" />
                    )}
                    {emailViaHead && <em style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginLeft: 2 }}>(via {memberName})</em>}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        <td style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', paddingLeft: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {memberName}
        </td>

        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>
          {addrLabel
            ? <span title={addrLabel + (addr ? ` — ${addr}` : '')}><FiMapPin size={11} style={{ marginRight: 4, flexShrink: 0 }} />{addrLabel}{addr ? ` — ${addr}` : ''}</span>
            : addr
              ? <span title={addr}><FiMapPin size={11} style={{ marginRight: 4, flexShrink: 0 }} />{addr}</span>
              : <span style={{ color: 'var(--color-text-muted)' }}>—</span>
          }
        </td>

      </tr>

      {isExpanded && (
        <tr className={styles.detailsRow}>
          <td colSpan={4}>
            <div className={styles.memberDetails} style={{ paddingLeft: 20 }}>
              <div className={styles.detailsGrid}>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Belongs To Member</span>
                  <span className={styles.detailsValue}><strong>{memberName}</strong></span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Phone</span>
                  <span className={styles.detailsValue}>
                    {person.phone
                      ? <><FiPhone size={11} style={{ marginRight: 4 }} />{person.phone}</>
                      : phoneViaHead
                        ? <><FiPhone size={11} style={{ marginRight: 4 }} />{person.createdByMember.phone} <em style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>(via {memberName})</em></>
                        : '—'}
                    {phone && (person.isPhoneVerified ? (
                      <span style={{ color: 'var(--color-success)', marginLeft: 6 }} title="Phone verified">
                        <FiCheckCircle size={11} /> Verified
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-danger)', marginLeft: 6 }} title="Phone not verified">
                        <FiAlertCircle size={11} /> Not verified
                      </span>
                    ))}
                  </span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Email</span>
                  <span className={styles.detailsValue}>
                    {person.email
                      ? <><FiMail size={11} style={{ marginRight: 4 }} />{person.email}</>
                      : emailViaHead
                        ? <><FiMail size={11} style={{ marginRight: 4 }} />{person.createdByMember.auth.email} <em style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>(via {memberName})</em></>
                        : '—'}
                    {email && (person.isEmailVerified ? (
                      <span style={{ color: 'var(--color-success)', marginLeft: 6 }} title="Email verified">
                        <FiCheckCircle size={11} /> Verified
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-danger)', marginLeft: 6 }} title="Email not verified">
                        <FiAlertCircle size={11} /> Not verified
                      </span>
                    ))}
                  </span>
                </div>
                {person.panCard && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>PAN Card</span>
                    <span className={styles.detailsValue}>{person.panCard}</span>
                  </div>
                )}
                {person.aadhaarCard && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Aadhaar</span>
                    <span className={styles.detailsValue}>{person.aadhaarCard}</span>
                  </div>
                )}
                <div className={`${styles.detailsItem} ${styles.detailsFullRow}`}>
                  <span className={styles.detailsLabel}>
                    Address {addrLabel && <em style={{ fontWeight: 400, fontSize: '0.8rem' }}>({addrLabel})</em>}
                  </span>
                  <span className={styles.detailsValue}>
                    <FiMapPin size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    {addr || <span className={styles.detailsValueEmpty}>No address on record</span>}
                  </span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// -- MemberRow -----------------------------------------------------------------
const MemberRow = ({ member, index, isExpanded, onToggle }) => {
  const addr = formatAddress(member);
  const joinedDate = member.joinedAt ? formatDate(normalizeDateOnly(member.joinedAt)) : '—';
  const hasDistinctPersonalEmail = member.personalEmail && member.personalEmail !== member.email;

  return (
    <React.Fragment>
      <tr className={styles.memberRowClickable} onClick={onToggle}>
        <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{index + 1}</td>
        <td>
          <div className={styles.memberCell}>
            <div className={styles.memberAvatar}>{getInitials(member.name)}</div>
            <div className={styles.memberDropdownItemInfo}>
              <div className={styles.memberDropdownItemName}>{member.name}</div>
              <div className={styles.memberDropdownItemEmail}>
                {member.phone && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiPhone size={10} /> {member.phone}
                    {member.isPhoneVerified ? (
                      <FiCheckCircle size={10} style={{ color: 'var(--color-success)' }} title="Phone verified" />
                    ) : (
                      <FiAlertCircle size={10} style={{ color: 'var(--color-danger)' }} title="Phone not verified" />
                    )}
                  </span>
                )}
                {member.phone && member.email && <span style={{ opacity: 0.4, margin: '0 4px' }}>·</span>}
                {member.email && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiMail size={10} /> {member.email}
                    {member.isEmailVerified && (
                      <span style={{ color: 'var(--color-success)', marginLeft: 2 }} title="Verified">
                        <FiCheckCircle size={10} />
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        <td style={{ paddingLeft: 8 }}>
          <span className={styles.memberRoleBadge}>{member.role || 'member'}</span>
        </td>

        <td style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', paddingLeft: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <FiCalendar size={10} /> {joinedDate}
          </span>
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.detailsRow}>
          <td colSpan={4}>
            <div className={styles.memberDetails} style={{ paddingLeft: 20 }}>
              <div className={styles.detailsGrid}>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Email</span>
                  <span className={styles.detailsValue}>
                    {member.email
                      ? <><FiMail size={11} style={{ marginRight: 4 }} />{member.email}</>
                      : '—'}
                    {member.isEmailVerified && (
                      <span style={{ color: 'var(--color-success)', marginLeft: 6 }} title="Email verified">
                        <FiCheckCircle size={11} /> Verified
                      </span>
                    )}
                  </span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Phone</span>
                  <span className={styles.detailsValue}>
                    {member.phone
                      ? <><FiPhone size={11} style={{ marginRight: 4 }} />{member.phone}</>
                      : '—'}
                    {member.phone && (member.isPhoneVerified ? (
                      <span style={{ color: 'var(--color-success)', marginLeft: 6 }} title="Phone verified">
                        <FiCheckCircle size={11} /> Verified
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-danger)', marginLeft: 6 }} title="Phone not verified">
                        <FiAlertCircle size={11} /> Not verified
                      </span>
                    ))}
                  </span>
                </div>
                {hasDistinctPersonalEmail && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Personal Email</span>
                    <span className={styles.detailsValue}>{member.personalEmail}</span>
                  </div>
                )}
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Role</span>
                  <span className={styles.detailsValue}>
                    <span className={styles.memberRoleBadge}>{member.role || 'member'}</span>
                  </span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Joined</span>
                  <span className={styles.detailsValue}>{joinedDate}</span>
                </div>
                {member.familyId && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Family Group</span>
                    <span className={styles.detailsValue}>#{member.familyId}</span>
                  </div>
                )}
                <div className={`${styles.detailsItem} ${styles.detailsFullRow}`}>
                  <span className={styles.detailsLabel}>Address</span>
                  <span className={styles.detailsValue}>
                    <FiMapPin size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    {addr || <span className={styles.detailsValueEmpty}>No address on record</span>}
                  </span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// -- AddPersonModal ------------------------------------------------------------
const AddPersonModal = ({ derasarId, membersList, onClose, onSave, loading }) => {
  const [form, setForm] = useState({ ...EMPTY_PERSON_FORM });
  const [addresses, setAddresses] = useState([]);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const addSpecialDate = () =>
    setForm((f) => ({ ...f, specialDates: [...f.specialDates, { label: '', date: '' }] }));

  const updateSpecialDate = (index, field, value) =>
    setForm((f) => ({
      ...f,
      specialDates: f.specialDates.map((d, i) => (i === index ? { ...d, [field]: value } : d)),
    }));

  const removeSpecialDate = (index) =>
    setForm((f) => ({ ...f, specialDates: f.specialDates.filter((_, i) => i !== index) }));

  // Load ALL derasar addresses once on mount so admin can assign any address.
  useEffect(() => {
    const loadAddresses = async () => {
      if (!derasarId) return;
      try {
        const res = await getAddressBookList(derasarId); // no memberId → all derasar addresses
        const nextAddresses = Array.isArray(res.data) ? res.data : [];
        setAddresses(nextAddresses);
        // Admin selects the address manually — no default auto-selection.
      } catch {
        setAddresses([]);
      }
    };

    loadAddresses();
  }, [derasarId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.headUserId) return toast.error('Please select a member first');
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.addressBookId) return toast.error('Please select an address');
    if (form.specialDates.some((d) => Boolean(d.label.trim()) !== Boolean(d.date)))
      return toast.error('Each special date requires both a label and a date');
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800, overflow: 'visible', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className={styles.modalHeader}>
          <div>
            <h3 style={{ margin: 0 }}>Add Person</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
              Select a derasar member and fill in the person's details.
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ overflow: 'visible' }}>
          {/* Member selector */}
          <div className="form-group">
            <label>Belongs To Member *</label>
            <SearchableMemberSelect
              members={membersList}
              value={form.headUserId}
              onChange={(val) => setForm((f) => ({ ...f, headUserId: String(val), addressBookId: '' }))}
              placeholder="Search derasar member…"
            />
            {form.headUserId && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 4, display: 'block' }}>
                This person will be saved under the selected member's account
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Full Name *</label>
            <input required placeholder="e.g. Rahul Shah" value={form.name} onChange={set('name')} />
          </div>

          <div className="form-group">
            <label>Address</label>
            <select value={form.addressBookId} onChange={set('addressBookId')} style={{ marginBottom: 6 }}>
              <option value="">— Pick from address book —</option>
              {addresses.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.memberOwner?.name ? `${a.memberOwner.name}: ` : ''}{a.label} — {formatAddress(a) || 'No address details'}
                </option>
              ))}
            </select>
            {addresses.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                No addresses in the derasar address book yet. Add one in Address Book first.
              </span>
            )}
          </div>

          <div className={styles.pairGrid}>
            <div className="form-group">
              <label>Phone</label>
              <input type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={set('phone')} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" placeholder="contact@example.com" value={form.email} onChange={set('email')} />
            </div>
          </div>

          <div className={styles.pairGrid}>
            <div className="form-group">
              <label>PAN Card</label>
              <input placeholder="Optional" value={form.panCard} onChange={set('panCard')} />
            </div>
            <div className="form-group">
              <label>Aadhaar Card</label>
              <input placeholder="Optional" value={form.aadhaarCard} onChange={set('aadhaarCard')} />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiGift size={13} /> Special Dates
            </label>
            <datalist id="adminSpecialDateLabels">
              <option value="Birthday" />
              <option value="Anniversary" />
              <option value="Death Anniversary" />
            </datalist>
            {form.specialDates.map((d, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input
                  placeholder="Label (e.g. Birthday)"
                  list="adminSpecialDateLabels"
                  value={d.label}
                  onChange={(e) => updateSpecialDate(index, 'label', e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  type="date"
                  value={d.date}
                  onChange={(e) => updateSpecialDate(index, 'date', e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => removeSpecialDate(index)}
                  aria-label="Remove date"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 32, height: 32, flexShrink: 0,
                    border: '1.5px solid var(--color-border)', borderRadius: 8,
                    background: 'var(--color-surface)', color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#c62828'; e.currentTarget.style.borderColor = '#c62828'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                >
                  <FiX size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={addSpecialDate}
              style={{ marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <FiPlus size={12} /> Add Date
            </button>
          </div>

          <div className={styles.modalActions} style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Add Person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -- GuestModal ----------------------------------------------------------------
const GuestModal = ({ editingGuest, form, setForm, onSubmit, onClose, loading }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
      <div className={styles.modalHeader}>
        <h3>{editingGuest ? 'Edit Pre-registered Member' : 'Add Member'}</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX size={16} /></button>
      </div>
      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label>Full Name *</label>
          <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahul Shah" />
        </div>
        <div className="form-group">
          <label>Email Address *</label>
          <input required type="email" value={form.email} disabled={!!editingGuest}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="rahul@example.com" />
        </div>
        <div className="form-group">
          <label>Mobile Number</label>
          <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} placeholder="e.g. Sheth family, annual donor" />
        </div>
        <div className={styles.modalActions}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : editingGuest ? 'Save Changes' : 'Add Member'}
          </button>
        </div>
      </form>
    </div>
  </div>
);

// -- DeleteGuestModal ----------------------------------------------------------
const DeleteGuestModal = ({ onClose, onConfirm }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
      <div className={styles.modalHeader}>
        <h3>Remove Pre-registered Member</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX size={16} /></button>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
        Remove this person from the pre-registered list? They will need to join manually.
      </p>
      <div className={styles.modalActions}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={onConfirm}>Remove</button>
      </div>
    </div>
  </div>
);

// -- Main Component ------------------------------------------------------------
const UsersPage = () => {
  const { activeDerasarId, isDerasarAdmin, can } = useAuth();

  const [persons, setPersons]               = useState([]);
  const [membersList, setMembersList]       = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [guestMembers, setGuestMembers]     = useState([]);
  const [derasarInfo, setDerasarInfo]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState(null);
  const [personSearch, setPersonSearch]     = useState('');
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [personsViewMode, setPersonsViewMode] = useState('owner');
  const [exportingExcel, setExportingExcel]   = useState(false);
  const [showDirPdfModal, setShowDirPdfModal] = useState(false);
  const [dirPdfProps, setDirPdfProps]         = useState(null);
  const [loadingDirPdf, setLoadingDirPdf]     = useState(false);

  // Add person modal
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [personFormLoading, setPersonFormLoading] = useState(false);

  // Guest modal
  const [showGuestModal, setShowGuestModal]   = useState(false);
  const [guestForm, setGuestForm]             = useState({ name: '', email: '', phone: '', notes: '' });
  const [guestFormLoading, setGuestFormLoading] = useState(false);
  const [editingGuest, setEditingGuest]       = useState(null);
  const [deleteGuestId, setDeleteGuestId]     = useState(null);

  // Members tab
  const [activeTab, setActiveTab]                   = useState('persons');
  const [membersList2, setMembersList2]             = useState([]);
  const [membersLoaded, setMembersLoaded]           = useState(false);
  const [membersLoading, setMembersLoading]         = useState(false);
  const [memberSearch, setMemberSearch]             = useState('');
  const [expandedMemberId, setExpandedMemberId]     = useState(null);
  const [showMembersPdfModal, setShowMembersPdfModal] = useState(false);
  const [membersPdfProps, setMembersPdfProps]       = useState(null);
  const [exportingMembersExcel, setExportingMembersExcel] = useState(false);

  // -- Fetch ----------------------------------------------------------------
  const fetchData = async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const calls = [
        getDerasarPersonsAdmin(activeDerasarId),
        getUsers({ derasarId: activeDerasarId }),
        getDerasar(activeDerasarId),
      ];
      if (isDerasarAdmin) calls.push(getGuestMembers(activeDerasarId));
      const [personsRes, usersRes, derasarRes, guestRes] = await Promise.all(calls);

      setPersons(Array.isArray(personsRes.data) ? personsRes.data : []);
      setMembersList(Array.isArray(usersRes.data) ? usersRes.data : []);
      setPendingRequests(derasarRes.data.pendingRequests || []);
      setDerasarInfo(derasarRes.data || null);
      if (guestRes) setGuestMembers(guestRes.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [activeDerasarId]);

  useEffect(() => {
    setMembersLoaded(false);
    setMembersList2([]);
    setActiveTab('persons');
    setMemberSearch('');
    setExpandedMemberId(null);
  }, [activeDerasarId]);

  const fetchMembers = async () => {
    if (!activeDerasarId || membersLoaded) return;
    setMembersLoading(true);
    try {
      const res = await getDerasarMembers(activeDerasarId);
      setMembersList2(Array.isArray(res.data) ? res.data : []);
      setMembersLoaded(true);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  // -- Search filter --------------------------------------------------------
  const filteredPersons = useMemo(() => {
    const q = personSearch.toLowerCase().trim();
    if (!q) return persons;
    return persons.filter((p) => {
      const memberName = p.createdByMember?.name || '';
      const addr = resolveAddress(p);
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.phone || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        memberName.toLowerCase().includes(q) ||
        addr.toLowerCase().includes(q)
      );
    });
  }, [persons, personSearch]);

  const groupedByOwner = useMemo(() => {
    const groups = {};
    for (const p of filteredPersons) {
      const ownerId = String(p.createdByMemberId || '__unknown__');
      if (!groups[ownerId]) {
        groups[ownerId] = {
          ownerId,
          ownerName: p.createdByMember?.name || `#${p.createdByMemberId}`,
          ownerEmail: p.createdByMember?.auth?.email || null,
          ownerPhone: p.createdByMember?.phone || null,
          persons: [],
        };
      }
      groups[ownerId].persons.push(p);
    }
    return Object.values(groups).sort((a, b) => a.ownerName.localeCompare(b.ownerName));
  }, [filteredPersons]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.toLowerCase().trim();
    if (!q) return membersList2;
    return membersList2.filter((m) =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.phone || '').toLowerCase().includes(q)
    );
  }, [membersList2, memberSearch]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'members' && !membersLoaded) fetchMembers();
  };

  const handleMembersPdf = () => {
    const members = filteredMembers.map((m) => ({
      ...m,
      joinedAtFormatted: m.joinedAt ? formatDate(normalizeDateOnly(m.joinedAt)) : '—',
    }));
    setMembersPdfProps({ derasar: derasarInfo || { name: '' }, members });
    setShowMembersPdfModal(true);
  };

  const handleMembersExcel = async () => {
    setExportingMembersExcel(true);
    try {
      const rows = filteredMembers.map((m, i) => ({
        no:      i + 1,
        name:    m.name    || '',
        email:   m.email   || '',
        phone:   m.phone   || '',
        role:    m.role    || '',
        joined:  m.joinedAt ? formatDate(normalizeDateOnly(m.joinedAt)) : '',
        address: [
          m.flatNo && m.buildingName ? `${m.flatNo}, ${m.buildingName}` : (m.flatNo || m.buildingName),
          m.addressLine1, m.addressLine2,
          m.city, m.state,
          m.pincode,
        ].filter(Boolean).join(', '),
      }));
      const res = await exportReportTable({
        format:    'excel',
        title:     'Derasar Members List',
        derasarId: activeDerasarId,
        columns: [
          { key: 'no',      label: '#',       type: 'number' },
          { key: 'name',    label: 'Name',    weight: 1.4 },
          { key: 'email',   label: 'Email',   weight: 1.8 },
          { key: 'phone',   label: 'Phone',   weight: 1.1 },
          { key: 'role',    label: 'Role',    weight: 0.8 },
          { key: 'joined',  label: 'Joined',  weight: 0.9 },
          { key: 'address', label: 'Address', weight: 2.2 },
        ],
        rows,
        totals: { name: `Total: ${rows.length} members` },
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `derasar-members-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Failed to export Excel');
    } finally {
      setExportingMembersExcel(false);
    }
  };

  // -- Handlers -------------------------------------------------------------
  const handleApproval = async (userId, action) => {
    setActionLoading(userId);
    try {
      await approveDerasarMember(activeDerasarId, userId, action);
      toast.success(`Member ${action}d`);
      fetchData();
    } catch {
      toast.error('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePersonSave = async (form) => {
    setPersonFormLoading(true);
    try {
      const specialDates = (form.specialDates || [])
        .map((d) => ({ label: (d.label || '').trim(), date: d.date || '' }))
        .filter((d) => d.label && d.date);

      await createDerasarPerson({
        derasarId: activeDerasarId,
        createdByMemberId: form.headUserId,
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        panCard: form.panCard || null,
        aadhaarCard: form.aadhaarCard || null,
        addressBookId: form.addressBookId || null,
        specialDates,
        flatNo: '',
        buildingName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      });
      toast.success('Person added');
      setShowPersonModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add person');
    } finally {
      setPersonFormLoading(false);
    }
  };

  const openAddGuest = () => { setEditingGuest(null); setGuestForm({ name: '', email: '', phone: '', notes: '' }); setShowGuestModal(true); };
  const openEditGuest = (g) => { setEditingGuest(g); setGuestForm({ name: g.name, email: g.email, phone: g.phone || '', notes: g.notes || '' }); setShowGuestModal(true); };

  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    setGuestFormLoading(true);
    try {
      if (editingGuest) {
        await updateGuestMember(editingGuest.id, { name: guestForm.name, phone: guestForm.phone, notes: guestForm.notes });
      } else {
        await addGuestMember({ derasarId: activeDerasarId, ...guestForm });
      }
      const res = await getGuestMembers(activeDerasarId);
      setGuestMembers(res.data);
      setShowGuestModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving member');
    } finally {
      setGuestFormLoading(false);
    }
  };

  const handleDeleteGuest = async () => {
    try {
      await deleteGuestMember(deleteGuestId);
      const res = await getGuestMembers(activeDerasarId);
      setGuestMembers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error removing member');
    } finally {
      setDeleteGuestId(null);
    }
  };

  const handleDirPdf = async () => {
    setLoadingDirPdf(true);
    try {
      const groups = await getMemberDirectory(activeDerasarId);
      setDirPdfProps({ derasar: derasarInfo || { name: '' }, groups });
      setShowDirPdfModal(true);
    } catch {
      toast.error('Failed to load directory data');
    } finally {
      setLoadingDirPdf(false);
    }
  };

  const handleDirExcel = async () => {
    setExportingExcel(true);
    try {
      const groups = await getMemberDirectory(activeDerasarId);
      let rowNo = 0;
      const rows = groups.flatMap((g) =>
        g.persons.map((p) => ({
          no:      ++rowNo,
          owner:   g.ownerName,
          name:    p.name  || '',
          phone:   p.phone || g.ownerPhone || '',
          email:   p.email || g.ownerEmail || '',
          address: [
            p.flatNo && p.buildingName
              ? `${p.flatNo}, ${p.buildingName}`
              : (p.flatNo || p.buildingName),
            p.addressLine1,
            p.city,
          ].filter(Boolean).join(', '),
        }))
      );
      const res = await exportReportTable({
        format:    'excel',
        title:     'Member Directory',
        derasarId: activeDerasarId,
        columns: [
          { key: 'no',      label: '#',       type: 'number' },
          { key: 'owner',   label: 'Owner',   type: 'text'   },
          { key: 'name',    label: 'Name',    type: 'text'   },
          { key: 'phone',   label: 'Phone',   type: 'text'   },
          { key: 'email',   label: 'Email',   type: 'text'   },
          { key: 'address', label: 'Address', type: 'text'   },
        ],
        rows,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `member-directory-${Date.now()}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Failed to export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  // -- Guards ---------------------------------------------------------------
  if (!activeDerasarId) return <div className="empty-state"><p>Select a derasar</p></div>;
  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const unlinkedGuests = guestMembers.filter((g) => g.status !== 'linked');

  return (
    <div className={styles.membersContainer}>
      <div className="page-header"><h2>Members</h2></div>

      {/* Tab bar — admin only */}
      {isDerasarAdmin && (
        <div className={styles.tabBar}>
          <button
            className={`${styles.tab} ${activeTab === 'persons' ? styles.tabActive : ''}`}
            onClick={() => handleTabChange('persons')}
          >
            Persons
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'members' ? styles.tabActive : ''}`}
            onClick={() => handleTabChange('members')}
          >
            Members
          </button>
        </div>
      )}

      {/* Pending Requests */}
      {isDerasarAdmin && pendingRequests.length > 0 && can('members', 'manage') && (
        <PendingRequestsSection
          requests={pendingRequests}
          actionLoading={actionLoading}
          onApprove={handleApproval}
        />
      )}

      {/* ── Persons tab content ─────────────────────────────────────────── */}
      {(!isDerasarAdmin || activeTab === 'persons') && (
        <>
          {/* -- Derasar Persons -- */}
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <FiUsers size={15} />
              Derasar Persons
              <span className={styles.countBadge}>{filteredPersons.length}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                <div className={styles.viewToggle}>
                  <button
                    className={`${styles.viewToggleBtn} ${personsViewMode === 'owner' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setPersonsViewMode('owner')}
                    title="Group by owner"
                  >
                    By Owner
                  </button>
                  <button
                    className={`${styles.viewToggleBtn} ${personsViewMode === 'list' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setPersonsViewMode('list')}
                    title="Flat list"
                  >
                    List
                  </button>
                </div>
                {isDerasarAdmin && (
                  <>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={handleDirPdf}
                      disabled={loadingDirPdf}
                      title="PDF Preview"
                    >
                      <FiFileText size={13} /> {loadingDirPdf ? 'Loading…' : 'PDF'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={handleDirExcel}
                      disabled={exportingExcel}
                      title="Export Excel"
                    >
                      <FiDownload size={13} /> {exportingExcel ? 'Exporting…' : 'Excel'}
                    </button>
                    {can('members', 'manage') && (
                      <button className="btn btn-primary btn-sm" onClick={() => setShowPersonModal(true)}>
                        <FiPlus size={13} /> Add Person
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search by name, phone, email, member, address…"
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
              />
            </div>

            {filteredPersons.length === 0 ? (
              <div className="empty-state">
                <p>{personSearch ? 'No persons match your search' : 'No persons yet'}</p>
              </div>
            ) : personsViewMode === 'owner' ? (
              <div>
                {groupedByOwner.map((group) => (
                  <div key={group.ownerId} className={styles.addressGroup}>
                    <div className={styles.addressGroupHeader}>
                      <div className={styles.memberAvatar} style={{ flexShrink: 0 }}>{getInitials(group.ownerName)}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                        <strong style={{ fontSize: '0.875rem', color: 'var(--color-text)' }}>{group.ownerName}</strong>
                        <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', display: 'flex', gap: 10 }}>
                          {group.ownerEmail && <span><FiMail size={10} style={{ marginRight: 3 }} />{group.ownerEmail}</span>}
                          {group.ownerPhone && <span><FiPhone size={10} style={{ marginRight: 3 }} />{group.ownerPhone}</span>}
                        </span>
                      </div>
                      <span className={styles.countBadge}>{group.persons.length}</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className={styles.memberTable} style={{ tableLayout: 'fixed', width: '100%', minWidth: 480 }}>
                        <colgroup>
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '26%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '24%' }} />
                          <col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>No.</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            <th>Email</th>
                            <th>Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.persons.map((p, index) => {
                            const phone = p.phone || p.createdByMember?.phone;
                            const email = p.email || p.createdByMember?.auth?.email;
                            const addr  = resolveAddress(p);
                            const addrLabel = resolveAddressLabel(p);
                            const isExpanded = expandedPersonId === p.id;
                            return (
                              <React.Fragment key={p.id}>
                                <tr className={styles.memberRowClickable} onClick={() => setExpandedPersonId((prev) => prev === p.id ? null : p.id)}>
                                  <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{index + 1}</td>
                                  <td>
                                    <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{p.name}</div>
                                  </td>
                                  <td style={{ fontSize: '0.85rem', color: phone ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                                    {phone ? (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        {phone}
                                        {p.isPhoneVerified ? (
                                          <FiCheckCircle size={11} style={{ color: 'var(--color-success)' }} title="Phone verified" />
                                        ) : (
                                          <FiAlertCircle size={11} style={{ color: 'var(--color-danger)' }} title="Phone not verified" />
                                        )}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td style={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: email ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                                    {email ? (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        {email}
                                        {p.isEmailVerified ? (
                                          <FiCheckCircle size={11} style={{ color: 'var(--color-success)' }} title="Email verified" />
                                        ) : (
                                          <FiAlertCircle size={11} style={{ color: 'var(--color-danger)' }} title="Email not verified" />
                                        )}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {addrLabel
                                      ? <span title={addr}><strong>{addrLabel}</strong>{addr ? ` — ${addr}` : ''}</span>
                                      : addr || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className={styles.detailsRow}>
                                    <td colSpan={5}>
                                      <div className={styles.memberDetails} style={{ paddingLeft: 20 }}>
                                        <div className={styles.detailsGrid}>
                                          {p.panCard && (
                                            <div className={styles.detailsItem}>
                                              <span className={styles.detailsLabel}>PAN Card</span>
                                              <span className={styles.detailsValue}>{p.panCard}</span>
                                            </div>
                                          )}
                                          {p.aadhaarCard && (
                                            <div className={styles.detailsItem}>
                                              <span className={styles.detailsLabel}>Aadhaar</span>
                                              <span className={styles.detailsValue}>{p.aadhaarCard}</span>
                                            </div>
                                          )}
                                          <div className={`${styles.detailsItem} ${styles.detailsFullRow}`}>
                                            <span className={styles.detailsLabel}>
                                              Address {addrLabel && <em style={{ fontWeight: 400, fontSize: '0.8rem' }}>({addrLabel})</em>}
                                            </span>
                                            <span className={styles.detailsValue}>
                                              <FiMapPin size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                              {addr || <span className={styles.detailsValueEmpty}>No address on record</span>}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.memberTableContainer} style={{ overflowX: 'auto' }}>
                <table className={styles.memberTable} style={{ tableLayout: 'fixed', width: '100%', minWidth: 520 }}>
                  <colgroup>
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '32%' }} />
                    <col style={{ width: '18%' }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Person</th>
                      <th style={{ paddingLeft: 12 }}>Owner</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersons.map((p, index) => (
                      <PersonRow
                        key={p.id}
                        person={p}
                        index={index}
                        isExpanded={expandedPersonId === p.id}
                        onToggle={() => setExpandedPersonId((prev) => prev === p.id ? null : p.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* -- Pre-registered (Add Member invite) -- */}
          {isDerasarAdmin && (
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <FiUserPlus size={15} />
                Pre-registered Members
                <span className={styles.countBadge}>{unlinkedGuests.length}</span>
                <div style={{ marginLeft: 'auto' }}>
                  {can('members', 'manage') && (
                    <button className="btn btn-primary btn-sm" onClick={openAddGuest}>
                      <FiUserPlus size={13} /> Add Member
                    </button>
                  )}
                </div>
              </div>

              {unlinkedGuests.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <p>No pre-registered members</p>
                </div>
              ) : (
                <div className={styles.memberTableContainer}>
                  <table className={styles.memberTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th className={styles.actionsCell} />
                      </tr>
                    </thead>
                    <tbody>
                      {unlinkedGuests.map((g) => (
                        <tr key={g.id} className={styles.guestRow}>
                          <td>
                            <div className={styles.memberCell}>
                              <div className={`${styles.memberAvatar} ${styles.guestAvatar}`}>{getInitials(g.name)}</div>
                              <div className={styles.memberDropdownItemInfo}>
                                <div className={styles.memberDropdownItemName}>{g.name}</div>
                                <div className={styles.memberDropdownItemEmail}>{g.email}{g.phone ? ` · ${g.phone}` : ''}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={styles.guestBadge}>
                              {g.status === 'awaiting_approval' ? 'Awaiting approval' : 'Not yet registered'}
                            </span>
                          </td>
                          <td className={styles.actionsCell}>
                            {g.status === 'pending' && can('members', 'manage') && (
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => openEditGuest(g)} title="Edit"><FiEdit2 size={12} /></button>
                                <button className="btn btn-ghost btn-sm" onClick={() => setDeleteGuestId(g.id)} title="Remove" style={{ color: 'var(--color-danger)' }}>
                                  <FiTrash2 size={12} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Members tab content ──────────────────────────────────────────── */}
      {isDerasarAdmin && activeTab === 'members' && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <FiUsers size={15} />
            Derasar Members
            <span className={styles.countBadge}>{filteredMembers.length}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleMembersPdf}
                disabled={membersLoading || filteredMembers.length === 0}
                title="Download PDF"
              >
                <FiFileText size={14} /> PDF
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleMembersExcel}
                disabled={exportingMembersExcel || filteredMembers.length === 0}
                title="Download Excel"
              >
                <FiDownload size={14} /> {exportingMembersExcel ? 'Exporting…' : 'Excel'}
              </button>
            </div>
          </div>

          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by name, email, phone…"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
            />
          </div>

          {membersLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : filteredMembers.length === 0 ? (
            <div className="empty-state">
              <p>{memberSearch ? 'No members match your search' : 'No members yet'}</p>
            </div>
          ) : (
            <div className={styles.memberTableContainer} style={{ overflowX: 'auto' }}>
              <table className={styles.memberTable} style={{ tableLayout: 'fixed', width: '100%', minWidth: 480 }}>
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '47%' }} />
                  <col style={{ width: '15%' }} />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Member</th>
                    <th style={{ paddingLeft: 8 }}>Role</th>
                    <th style={{ paddingLeft: 8 }}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m, index) => (
                    <MemberRow
                      key={m.id}
                      member={m}
                      index={index}
                      isExpanded={expandedMemberId === m.id}
                      onToggle={() => setExpandedMemberId((prev) => prev === m.id ? null : m.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* -- Modals -- */}
      {showPersonModal && (
        <AddPersonModal
          derasarId={activeDerasarId}
          membersList={membersList}
          onClose={() => setShowPersonModal(false)}
          onSave={handlePersonSave}
          loading={personFormLoading}
        />
      )}

      {showGuestModal && (
        <GuestModal
          editingGuest={editingGuest}
          form={guestForm} setForm={setGuestForm}
          onSubmit={handleGuestSubmit}
          onClose={() => setShowGuestModal(false)}
          loading={guestFormLoading}
        />
      )}

      {deleteGuestId && (
        <DeleteGuestModal
          onClose={() => setDeleteGuestId(null)}
          onConfirm={handleDeleteGuest}
        />
      )}

      {showDirPdfModal && dirPdfProps && (
        <PdfOptionsModal
          type="memberDirectory"
          documentProps={dirPdfProps}
          onClose={() => { setShowDirPdfModal(false); setDirPdfProps(null); }}
        />
      )}

      {showMembersPdfModal && membersPdfProps && (
        <PdfOptionsModal
          type="derasarMemberList"
          documentProps={membersPdfProps}
          onClose={() => { setShowMembersPdfModal(false); setMembersPdfProps(null); }}
        />
      )}
    </div>
  );
};

export default UsersPage;
